import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Header from "../../components/common/Header";

// GSAP plugins रजिस्टर करें (केवल एक बार)
gsap.registerPlugin(ScrollTrigger);

const PressReleaseCard = ({ release }) => {
  // Helper component for individual press cards
  return (
    <div
      key={release?.id}
      // एनिमेट करने के लिए क्लास जोड़ें: 'animated-press-card'
      className="animated-press-card flex flex-col justify-start items-center w-full lg:w-[508px] h-auto bg-background-overlay border-[3px] border-solid border-border-white rounded-[20px] md:rounded-2xl p-[8px] sm:p-[10px] lg:p-[10px] shadow-[0px_4px_21px_#888888ff] hover:shadow-lg transition-shadow duration-300"
    >
      {/* Image Section */}
      <div className="relative w-full">
        <img
          src={release?.image}
          alt={release?.title}
          className="w-full h-[180px] sm:h-[220px] lg:h-[248px] object-cover rounded-[10px] md:rounded-lg"
        />
      </div>

      {/* Content Section */}
      <div className="w-full mt-[10px] sm:mt-[12px] lg:mt-[14px] px-2 sm:px-4 lg:px-6 pb-2 sm:pb-4 lg:pb-6">
        <h3 className="text-[24px] font-[Parkinsans] font-bold leading-[26px] sm:leading-[30px] lg:leading-xl text-center text-text-white mb-1 sm:mb-2">
          {release?.title}
        </h3>
        <p className="text-[18px] font-[Parkinsans] font-normal leading-base text-center text-text-white">
          {release?.description}
        </p>
      </div>
    </div>
  );
};

const PressReleaseHero = ({ pressReleases }) => {
  // 1. Ref बनाएं
  const sectionRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    // सुनिश्चित करें कि ref मौजूद है
    if (!sectionRef.current) return;

    // 2. GSAP context बनाएं
    const context = gsap.context(() => {
      // --- 1. Hero Title Animation (Slide-in) ---
      if (titleRef.current) {
        gsap.fromTo(
          titleRef.current,
          {
            y: 50,
            opacity: 0,
            // आपके द्वारा दिए गए 'clipPath' स्टाइल को यहाँ लागू करना
            clipPath: "inset(100% 0 0% 0)",
          },
          {
            y: 0,
            opacity: 1,
            clipPath: "inset(0% 0 0% 0)",
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: sectionRef.current, // पूरे सेक्शन के दिखने पर ट्रिगर करें
              start: "top 80%", // जब सेक्शन 80% पर पहुंचे
              toggleActions: "play none none none",
            },
          }
        );
      }

      // --- 2. Press Release Cards Animation (Staggered Slide-in) ---
      // 'animated-press-card' क्लास वाले सभी कार्ड्स को target करें
      const cards = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-press-card")
      );

      gsap.from(cards, {
        y: 80, // नीचे से शुरू
        opacity: 0,
        stagger: 0.2, // कार्ड्स को बारी-बारी से एनिमेट करें
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: cards,
          start: "top 90%", // जब पहला कार्ड 90% पर पहुंचे
          toggleActions: "play none none none",
          // markers: true,
        },
      });
    }, sectionRef); // Pass the ref to the context

    // Cleanup function
    return () => context.revert();
  }, []); // Empty dependency array means this runs once

  return (
    // sectionRef को सबसे बाहरी तत्व पर लगाएं
    <div
      ref={sectionRef}
      className="relative w-full"
      style={{ backgroundImage: "url(/assets/gallery/PressRelease.png)" }}
    >
      {/* Header and Content Overlay */}
      <div className=" inset-0 flex flex-col justify-start items-center w-full h-full">
        <Header />
        {/* Header Section */}
        <div className="w-full bg-cover bg-center pb-[23px] sm:pb-[34px] lg:pb-[46px] h-full flex flex-col justify-between items-center">
          {/* Press Release Title */}
          <div className="flex justify-center mt-[80px] sm:mt-[100px] lg:mt-[135px]">
            {/* titleRef को यहां लगाएं */}
            <h1
              ref={titleRef}
              className="text-[40px] sm:text-[60px] md:text-[80px] lg:text-hero font-[Parkinsans] font-bold leading-[56px] sm:leading-[84px] md:leading-[112px] lg:leading-hero text-center text-[#343163]"
            >
              Press Release
            </h1>
          </div>
          <div className="w-full max-w-[1620px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 mt-[100px] sm:mt-[60px] lg:mt-[216px]">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-[48px] justify-start items-center">
              {pressReleases?.map((release) => (
                // PressReleaseCard में 'animated-press-card' क्लास जोड़ी गई है
                <PressReleaseCard key={release?.id} release={release} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay Gradients */}
      <div className="absolute bottom-0 left-0 w-full h-[260px] sm:h-[300px] lg:h-[382px] bg-[linear-gradient(180deg,#0f100900_0%,_#0f1009_100%)]"></div>
      <div className="absolute bottom-0 left-0 w-full h-[260px] sm:h-[120px] lg:h-[146px] bg-background-dark"></div>
    </div>
  );
};

export default PressReleaseHero;
